/**
 * **화면이 먼저 포기하지 않게**. 서버는 글이 버려지면 한 번 더 받느라(다시 받기) 오래 걸릴 수 있다
 * — 질문 타로·둘 중·10장·공부는 한 번에 최대 90초 × 두 번. 그 사이 연결이 끊기면 화면은 "글을 못 받았어요" 인데
 * 서버는 글을 저장하고 값을 그대로 두었다 (ko 3장 "헤어진 그 사람" 1건 — 지난 기록에서는 글이 보였다).
 *
 * 이제 **응답 없이 끊겼을 때만**(서버가 실패라고 답한 것이 아니라) 같은 판을 `?peek=1` 로 몇 번 더 물어,
 * 저장된 글이 있으면 그것을 보여 준다 — AI 도, 값도 없다. 서버가 실패라고 답했으면 이미 돌려준 뒤라 바로 실패.
 */
export const PEEK_DELAYS = [3_000, 10_000, 30_000, 60_000] as const

export async function recoverDropped<T>(first: T, dropped: (r: T) => boolean, peek: () => Promise<T>, delays: readonly number[] = PEEK_DELAYS, wait = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms))): Promise<T> {
  if (!dropped(first)) return first
  for (const ms of delays) {
    await wait(ms)
    const r = await peek()
    // 저장된 글이 왔으면 그것, 서버가 "아직 없다" 거나 또 끊겼으면 조금 더 기다린다
    if (!dropped(r) && !isNotSaved(r)) return r
  }
  return first
}

function isNotSaved(r: unknown): boolean {
  return Boolean(r && typeof r === 'object' && (r as { reason?: unknown }).reason === 'not_saved')
}

/**
 * 서버가 **"쓰는 중"**(`writing` — 같은 판을 다른 요청이 지금 쓰고 있다)이라 답했을 때 다시 읽는 간격.
 * 합쳐 약 100초다 — 서버 표시는 90초 뒤 풀린다. 다시 읽기는 `?peek=1` 이라 AI 도 값도 없다.
 * 그 사이 글이 저장되면 그 글(같은 글)을 보여 주고, 끝내 없으면 첫 답(`writing`)을 돌려줘 화면이 "다시 시도" 를 띄운다
 * (그때는 표시가 풀려 다시 쓸 수 있다). 기다리는 동안 화면은 그대로 기다림 표시(AiWaiting)다.
 */
export const WRITING_DELAYS = [3_000, 3_000, 4_000, 5_000, 5_000, 10_000, 10_000, 15_000, 15_000, 30_000] as const

function reasonOf(r: unknown): unknown {
  return r && typeof r === 'object' ? (r as { reason?: unknown }).reason : undefined
}

/** 끊겼으면(dropped) 또는 쓰는 중이면(writing) 같은 판을 `?peek=1` 로 다시 읽는다 — 둘 다 아니면 첫 답 그대로 */
export function recoverPending<T>(first: T, peek: () => Promise<T>, wait?: (ms: number) => Promise<void>): Promise<T> {
  if (reasonOf(first) === 'writing') {
    return recoverDropped(first, (r) => reasonOf(r) === 'writing' || reasonOf(r) === 'dropped', peek, WRITING_DELAYS, wait)
  }
  return recoverDropped(first, (r) => reasonOf(r) === 'dropped', peek, PEEK_DELAYS, wait)
}
